import { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import tradeFromScratchStore from "@/store/tradeFromScratchStore";
import { sendBondTicket } from "@/store/tradeFromScratchStoreACTIONS";
import BondCoreFields from "./bond/BondCoreFields";
import BondDetailFields from "./bond/BondDetailFields";
import { useBondTicket } from "./bond/useBondTicket";
import {
  returnHighlightedKeys,
  useTicketShell,
} from "./helper/useTicketShell";
import TicketStatusLine from "./ticketFields/TicketStatusLine";
import TicketHeader from "./ticketFields/TicketHeader";

// LQC-2254 - the Leg Creation bond ticket: header, core fields, detail fields, footer.
// Everything the user picks is resolved against the reference-data stores, so a
// security or portfolio the back end does not know about cannot be sent.

interface IBondTicketProps {
  ticketId: string;
}

const BondTicket = ({ ticketId }: IBondTicketProps) => {
  const { user, getAccessTokenSilently } = useAuth0();
  const shell = useTicketShell(ticketId);
  const { missingEntries } = useBondTicket(ticketId);
  const ensureBondTicket = tradeFromScratchStore(
    (state) => state.ensureBondTicket,
  );
  const resetBondTicket = tradeFromScratchStore(
    (state) => state.resetBondTicket,
  );

  useEffect(() => {
    ensureBondTicket(ticketId);
  }, [ticketId, ensureBondTicket]);

  const canSend = missingEntries.length === 0 && !shell.isSending;
  const highlightedKeys = returnHighlightedKeys(
    shell.isHoveringSend,
    canSend,
    missingEntries,
  );

  const handleSend = async () => {
    const sent = await sendBondTicket({
      ticketId,
      user,
      getAccessTokenSilently,
    });
    if (sent) shell.markSent();
  };

  return (
    <div
      data-testid="BondTicketContainer"
      id="BondTicketContainer"
      className="flex h-full w-full flex-col gap-2"
    >
      <TicketHeader
        testIdPrefix="BondTicket"
        instrument={shell.instrument}
        onInstrumentChange={shell.setInstrument}
        canSend={canSend}
        isSending={shell.isSending}
        isConfirming={shell.isConfirmingSend}
        onSend={handleSend}
        onClear={() => resetBondTicket(ticketId)}
        onSendHoverChange={shell.setIsHoveringSend}
      />
      <TicketStatusLine
        testIdPrefix="BondTicket"
        lastSent={shell.lastSent}
        missingFields={missingEntries.map((entry) => entry.label)}
        isBlocking={shell.isHoveringSend && !canSend}
      />
      <BondCoreFields ticketId={ticketId} highlightedKeys={highlightedKeys} />
      <BondDetailFields ticketId={ticketId} />
    </div>
  );
};

export default BondTicket;
